import { useState, type ChangeEvent, type FormEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import Button from '../../components/base/Button';
import Input from '../../components/base/Input';
import { logger } from '../../utils/logger';

type InstagramSignupForm = {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
  acceptTerms: boolean;
};

export default function InstagramSignupPage() {
  const navigate = useNavigate();

  const [formData, setFormData] = useState<InstagramSignupForm>({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    acceptTerms: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (field: keyof InstagramSignupForm) => (
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    const value = field === 'acceptTerms' ? event.target.checked : event.target.value;
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    logger.debug('[InstagramSignupPage] Iniciando cadastro pelo fluxo Instagram');

    setError('');

    if (!formData.acceptTerms) {
      logger.debug('[InstagramSignupPage] Termos não aceitos');
      setError('Você deve aceitar os Termos de Uso e Política de Privacidade para criar uma conta');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      logger.debug('[InstagramSignupPage] Senhas não coincidem');
      setError('As senhas não coincidem');
      return;
    }

    if (formData.password.length < 6) {
      logger.debug('[InstagramSignupPage] Senha muito curta');
      setError('A senha deve ter pelo menos 6 caracteres');
      return;
    }

    setLoading(true);

    try {
      logger.debug('[InstagramSignupPage] Chamando supabase.auth.signUp');
      logger.sensitive('[InstagramSignupPage] Dados de cadastro', {
        email: formData.email,
        name: formData.name,
      });

      const { error: signUpError } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          data: {
            full_name: formData.name,
            source: 'instagram',
            signup_date: new Date().toISOString(),
          },
          emailRedirectTo: `${window.location.origin}/instagram/confirmed`,
        },
      });

      if (signUpError) {
        throw signUpError;
      }

      logger.success('[InstagramSignupPage] Cadastro enviado, direcionando para email-sent');
      navigate('/instagram/email-sent', { state: { email: formData.email } });
    } catch (signUpError: any) {
      logger.error('[InstagramSignupPage] Erro ao criar usuário', signUpError);
      setError(signUpError?.message || 'Erro ao criar conta. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const goBack = () => {
    navigate('/instagram/onboarding');
  };

  return (
    <div className="min-h-screen bg-gradient-to-t from-green-200 to-white flex flex-col items-center justify-center px-6 py-8">
      <div className="w-full flex justify-start mb-6">
        <button
          onClick={goBack}
          className="w-10 h-10 bg-white rounded-2xl flex items-center justify-center shadow-md hover:bg-gray-50 transition-colors"
        >
          <i className="ri-arrow-left-line text-gray-600 text-xl"></i>
        </button>
      </div>

      <div className="text-center mb-8">
        <div className="flex justify-center items-center mb-4">
          <div className="w-24 h-24 bg-transparent rounded-3xl flex items-center justify-center shadow-lg overflow-hidden">
            <img
              src="/Imagens/logo 2.0.png"
              alt="Logo Magistral 2.0"
              className="w-full h-full object-contain"
              onError={(event) => {
                const target = event.target as HTMLImageElement;
                target.style.display = 'none';
                const fallbackDiv = document.createElement('div');
                fallbackDiv.className = 'w-20 h-20 bg-emerald-500 rounded-3xl flex items-center justify-center';
                const icon = document.createElement('i');
                icon.className = 'ri-heart-pulse-fill text-3xl text-white';
                fallbackDiv.appendChild(icon);
                target.parentNode?.appendChild(fallbackDiv);
              }}
            />
          </div>
        </div>
        <h1
          className="text-3xl font-bold text-gray-800 font-sans"
          style={{
            fontWeight: 700,
            textShadow: '1px 2px 4px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.05)',
          }}
        >
          Magistral
        </h1>
        <p className="text-gray-600 mt-2 font-sans" style={{ fontWeight: 500 }}>
          Vamos começar juntos!
        </p>
      </div>

      <div className="w-full max-w-sm bg-white rounded-3xl p-6 shadow-xl">
        <div className="text-center mb-6">
          <h2 className="text-xl font-semibold text-gray-800">Criar sua conta</h2>
          <p className="text-gray-500 text-sm mt-1">Preencha os dados para começar</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <Input
            label="Nome completo"
            type="text"
            placeholder="Seu nome completo"
            value={formData.name}
            onChange={handleInputChange('name')}
            required
          />

          <Input
            label="E-mail"
            type="email"
            placeholder="seu@email.com"
            value={formData.email}
            onChange={handleInputChange('email')}
            required
          />

          <Input
            label="Senha"
            type="password"
            placeholder="Crie uma senha"
            value={formData.password}
            onChange={handleInputChange('password')}
            required
          />

          <Input
            label="Confirmar Senha"
            type="password"
            placeholder="Confirme sua senha"
            value={formData.confirmPassword}
            onChange={handleInputChange('confirmPassword')}
            required
          />

          <div className="flex items-start space-x-3 mt-4">
            <input
              type="checkbox"
              id="acceptTerms"
              checked={formData.acceptTerms}
              onChange={handleInputChange('acceptTerms')}
              className="mt-1 w-4 h-4 text-emerald-600 bg-gray-100 border-gray-300 rounded focus:ring-emerald-500 focus:ring-2"
              required
            />
            <label htmlFor="acceptTerms" className="text-xs text-gray-600 leading-relaxed">
              Eu li e aceito os{' '}
              <Link to="/terms-and-privacy" className="text-emerald-500 hover:text-emerald-600 underline">
                Termos de Uso
              </Link>{' '}
              e{' '}
              <Link to="/terms-and-privacy" className="text-emerald-500 hover:text-emerald-600 underline">
                Política de Privacidade
              </Link>
            </label>
          </div>

          <Button type="submit" size="lg" className="w-full mt-6" disabled={loading}>
            {loading ? (
              <>
                <i className="ri-loader-4-line mr-2 animate-spin"></i>
                Criando conta...
              </>
            ) : (
              <>
                <i className="ri-user-add-line mr-2"></i>
                Criar Conta
              </>
            )}
          </Button>
        </form>
      </div>

      <div className="grid grid-cols-3 gap-4 mt-8 w-full max-w-sm">
        <div className="text-center">
          <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center mb-2 mx-auto shadow-md">
            <i className="ri-shield-check-line text-emerald-500 text-[1.67rem]"></i>
          </div>
          <p className="text-xs text-gray-600">Seguro</p>
        </div>
        <div className="text-center">
          <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center mb-2 mx-auto shadow-md">
            <img
              src="/Imagens/icons8-família-de-dois-homens-64.png"
              alt="Família"
              className="w-8 h-8"
            />
          </div>
          <p className="text-xs text-gray-600">Família</p>
        </div>
        <div className="text-center">
          <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center mb-2 mx-auto shadow-md">
            <i className="ri-heart-line text-emerald-500 text-[1.67rem]"></i>
          </div>
          <p className="text-xs text-gray-600">Saúde</p>
        </div>
      </div>
    </div>
  );
}


